/* GrowthOS — Template Gallery (route /settings/templateGallery).
   Rebuilt to the design's GalleryScreen (growthos/TemplateFlow.jsx → GalleryScreen):
   a module context bar + "Start from blank", type pills that jump to sections, and
   per-type sections (color dot + title + count + descriptor) each with a dashed "Blank"
   card + template cards (CSS email preview + hover "Use this"). Replaces the legacy
   left-nav scroll-spy + <AllTemplates> grid. Wired to the real API exactly as before:
   getTemplateByModule(module) returns groups [{templateType, data:[...]}]; picking a
   template → /settings/templateGallery/newTemplate with {module, data:{...,type:'add'}};
   blank → same route with {module}. Shared design primitives from ./templatePreview. */
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import apiServices from '../../../services/service';
import Loader from '../../../shared/component/Loader';
import { Ic, Preview, resolveType, modIcon } from './templatePreview';

const TemplateGallery = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [module, setModule] = useState(null);
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState([]);
  const [active, setActive] = useState(null);
  const secRefs = useRef({});

  useEffect(() => {
    if (location.state) {
      setModule(typeof location.state === 'string' ? location.state : location.state?.module || null);
    }
  }, [location]);

  useEffect(() => {
    if (!module) return;
    setLoading(true);
    apiServices
      .getTemplateByModule(module)
      .then((response) => {
        setLoading(false);
        if (response.status === 200) {
          const data = Array.isArray(response.data) ? response.data : [];
          setGroups(data);
          setActive(data[0]?.templateType || null);
        } else {
          toast.error(`An unexpected error occurred: ${response.status}`, { position: 'top-right' });
        }
      })
      .catch((error) => {
        console.error('Error fetching templates:', error);
        setLoading(false);
      });
  }, [module]);

  const totalCount = groups.reduce((n, g) => n + (g?.data?.length || 0), 0);

  const jump = (type) => {
    setActive(type);
    const el = secRefs.current[type];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const goBlank = () => navigate('/settings/templateGallery/newTemplate', { state: { module } });
  const goPick = (t) => navigate('/settings/templateGallery/newTemplate', { state: { module, data: { ...t, type: 'add' } } });

  return (
    <div className="inner-page">
      {loading && <Loader />}
      <div className="gos-tpl gos-tgal">
        {/* back */}
        <div className="gos-tgal-backrow">
          <button type="button" className="gos-btn gos-btn-ghost gos-btn-sm" onClick={() => navigate('/settings/templates')}>
            <Ic name="chevL" size={15} /> Templates
          </button>
        </div>

        {/* module context bar */}
        <div className="gos-tgal-context">
          <div className="gos-tgal-context-l">
            <span className="gos-tgal-ic"><Ic name={modIcon(module)} size={18} /></span>
            <div>
              <div className="gos-tgal-label">Starting a template for</div>
              <div className="gos-tgal-name">{module || '—'} module</div>
            </div>
          </div>
          <button type="button" className="gos-btn gos-btn-primary" onClick={goBlank}>
            <Ic name="plus" size={16} /> Start from blank
          </button>
        </div>

        {/* type pills */}
        {groups.length > 0 ? (
          <div className="gos-tgal-pills">
            {groups.map((g) => (
              <button
                type="button"
                key={g.templateType}
                className={'gos-tgal-pill' + (active === g.templateType ? ' on' : '')}
                onClick={() => jump(g.templateType)}
              >
                {g.templateType}
                <span className="gos-tgal-pill-count">{g?.data?.length || 0}</span>
              </button>
            ))}
          </div>
        ) : null}

        {/* sections */}
        {groups.length > 0 ? (
          groups.map((g) => {
            const meta = resolveType(g.templateType);
            const items = g?.data || [];
            return (
              <section className="gos-tgal-sec" key={g.templateType} ref={(el) => { secRefs.current[g.templateType] = el; }}>
                <div className="gos-tgal-sec-head">
                  <span className="gos-tgal-sec-dot" style={{ background: meta.color }} />
                  <span className="gos-tgal-sec-title">{g.templateType}</span>
                  <span className="gos-tgal-sec-count">{items.length}</span>
                  <span className="gos-tgal-sec-desc">{meta.desc}</span>
                </div>
                <div className="gos-tgal-grid">
                  <button type="button" className="gos-tgal-blank" onClick={goBlank}>
                    <span className="gos-tgal-blank-plus"><Ic name="plus" size={22} /></span>
                    <span>Blank {g.templateType}</span>
                  </button>
                  {items.map((t) => (
                    <div className="gos-tgal-card" key={t.id} onClick={() => goPick(t)}>
                      <div className="gos-tgal-thumb">
                        <Preview type={t.templateType || g.templateType} />
                        <div className="gos-tpl-ghover">
                          <button type="button" className="gos-btn gos-btn-primary gos-btn-sm" onClick={(e) => { e.stopPropagation(); goPick(t); }}>
                            Use this <Ic name="arrowR" size={14} />
                          </button>
                        </div>
                      </div>
                      <div className="gos-tgal-card-name">{t.name || 'Untitled template'}</div>
                    </div>
                  ))}
                </div>
              </section>
            );
          })
        ) : (
          !loading && module ? (
            <div className="gos-tpl-empty">No templates found for {module}. Start from blank to create one.</div>
          ) : null
        )}

        {groups.length > 0 && totalCount === 0 ? (
          <div className="gos-tpl-empty">No templates yet for {module}.</div>
        ) : null}
      </div>
    </div>
  );
};

export default TemplateGallery;
